  import React, { useContext, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import {
  DistributionTypeEnum,
  PageResponseQuotePreviewOutDTO,
  PriceTypeEnum,
  PseudoStateEnum, PseudoStatesEnum,
  QuotePreviewOutDTO,
  SortCriteria,
} from '@api/ringil-api.v2';
import { DemandListPseudoState } from '@type/enums';
import {
  dayjsFromApiPrague,
  dayjsFromApiUtc,
  formatDateTimeFromDayjsInTzWithLocale,
  stringIsoFromDateInTz,
  shortDateFormat,
  getBrowserTimezone, dayjsFromApiUtcInTz,
} from '@utils/dateWithTz.tsx';
import { ApiContext } from '@api/api';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import DemandStateSpan from '@components/obsolete/StateSpan/DemandStateSpan.tsx';
import ExpeditionListStart from '@components/obsolete/ExpeditionListStart/ExpeditionListStart.tsx';
import ExpeditionLocation from '@components/obsolete/ExpeditionLocation/ExpeditionLocation.tsx';
import NoteIcon from '@components/obsolete/Icons/NoteIcon.tsx';
import ExtraCostIcon from '@components/obsolete/Icons/ExtraCostIcon.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import NavTitle from '@components/obsolete/NavTitle/NavTitle.tsx';
import { FilterPage, useFilter } from '@hooks/useFilter/Filter.tsx';
import { Tooltip } from 'primereact/tooltip';
import { formatPrice } from '@utils/priceUtils.ts';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';

const Demands = () => {
  const { t }: { t: any } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const { caListDemands } = useContext(ApiContext);
  const [pageData, setPageData] = useState<PageResponseQuotePreviewOutDTO>(undefined);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const navigation = useNavigation();

  const sortCriteriaList: SortCriteria[] = [
    { field: 'lastUpdatedDateInUtc', direction: 'desc' },
    { field: 'createdDateInUtc', direction: 'desc' },
  ];

  const timezoneToUse = loggedUser?.defaultTimezoneId || 'Europe/Prague';

  // @ts-ignore
  const loadData = (pageSize, pageNumber, filter: FilterType) => {
    const normalizedFilter = {
      ...filter,
      userTimezoneId: getBrowserTimezone(),
    };
    if (!filter.userTimezoneId) {
      applyFilterValue('userTimezoneId', normalizedFilter.userTimezoneId);
    }
    caListDemands(
      {
        pseudoStates: normalizedFilter.pseudoStates,
        loadingDateFromISO: stringIsoFromDateInTz(normalizedFilter.loadingDate, timezoneToUse),
        loadingDateToISO: stringIsoFromDateInTz(normalizedFilter.loadingDate, timezoneToUse),
        unloadingDateFromISO: stringIsoFromDateInTz(normalizedFilter.unloadingDate, timezoneToUse),
        unloadingDateToISO: stringIsoFromDateInTz(normalizedFilter.unloadingDate, timezoneToUse),
        locationName: normalizedFilter.locationName,
        applicationId: normalizedFilter.applicationId,
        userTimezoneId: normalizedFilter.userTimezoneId,
      },
      { pageSize, pageNumber: pageNumber, sortCriteriaList: sortCriteriaList },
      data => {
        setPageData(data);
      },
    );
  };

  const onPage = (event: any) => {
    applyPaging({ pageNumber: event.page + 1, pageSize: event.rows });
  };

  const items = [
    {
      label: t(`Demands.Action`, `Akce`),
      icon: 'pi pi-refresh',
    },
  ];

  const stageBodyTemplate = (rowData: QuotePreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    return (
      <div data-testid={`carrier-demands-row-${applicationId}`}>
        <div className="id">
          {/*<Link href={`/carrier/demand/${rowData.companyId}-${rowData.applicationId}`}>*/}
          <Link
            to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierDemandDetailByQuoteId(rowData.quoteId))}>
            <a data-testid={`carrier-demands-open-${rowData.quoteId}`}>
              <small>{rowData.applicationId}</small>
            </a>
          </Link>
        </div>
        {rowData.pseudoState && (
          <div className="stage">
            <span
              data-testid={`carrier-demands-row-${applicationId}-status`}
              data-state={rowData.pseudoState}
            >
              <DemandStateSpan demandState={rowData.pseudoState} isManual={rowData.isManualExpedition} />
            </span>
          </div>
        )}
      </div>
    );
  };

  const typePriceBodyTemplate = (rowData: QuotePreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    let typePrice = null;
    switch (rowData.priceType) {
      case PriceTypeEnum.AGREED:
        typePrice =
          <strong className="text-green">{t(`Demands.priceType.${rowData.priceType}`, rowData.priceType)}</strong>;
        break;

      case PriceTypeEnum.REJECTED:
        typePrice =
          <strong className="text-pink">{t(`Demands.priceType.${rowData.priceType}`, rowData.priceType)}</strong>;
        break;

      default:
        typePrice = <strong>{t(`Demands.priceType.${rowData.priceType}`, rowData.priceType)}</strong>;
    }
    // CONTRACTED ("Smluvní dopravce"): the not-yet-offered price is the contracted one, not an expected price
    if (rowData.distributionType === DistributionTypeEnum.CONTRACTED && rowData.priceType === PriceTypeEnum.EXPECTED) {
      typePrice = <strong>{tr(`Demands.priceType.CONTRACTED`, `Smluvní cena`)}</strong>;
    }

    return (
      <div
        data-testid={`carrier-demands-row-${applicationId}-priceBlock`}
        data-price-type={rowData.priceType ?? ''}
      >
        {rowData.price ? (
          <div className="price">
            <span data-testid={`carrier-demands-row-${applicationId}-price`}>{formatPrice(rowData.price, rowData.currency)}</span>
          </div>
        ) : (
          <span data-testid={`carrier-demands-row-${applicationId}-price`}>—</span>
        )}

        {rowData.priceType && (
          <div className="type-price">
            <small data-testid={`carrier-demands-row-${applicationId}-priceType`} data-price-type={rowData.priceType}>
              {typePrice}
            </small>
          </div>
        )}
        {rowData.distributionType === DistributionTypeEnum.CONTRACTED && (
          <div>
            <small data-testid={`carrier-demands-row-${applicationId}-distributionType`} data-distribution-type={rowData.distributionType}>
              {tr(`Demands.distributionType.CONTRACTED`, `Smluvní dopravce`)}
            </small>
          </div>
        )}

        <div>
          {!rowData.isExpiredDistribution ? (
            <div className="distribution-wrapper">
              <small
                className="value"
                data-testid={`carrier-demands-row-${applicationId}-validTill`}
              >
                {t(`Demands.validUntilWithEscape`, `ValidUntil {{-date}}`, {
                  date: rowData.utcEnd ? formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(rowData.utcEnd), timezoneToUse)
                  : formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiPrague(rowData.distributionValidTill), timezoneToUse),
                })}
                <COTimezone dataTestId={`carrier-demands-row-${applicationId}-validTill-timezone`} timezoneId={timezoneToUse} />
              </small>
              ,<small className="place" data-testid={`carrier-demands-row-${applicationId}-customerCompanyName`}>{rowData.customerCompanyName}</small>
            </div>
          ) : (
            <div className="distribution-wrapper">
              <small
                className="value text-orange"
                data-testid={`carrier-demands-row-${applicationId}-validTill`}
              >
                {t(`Demands.validUntilWithEscape`, `ValidUntil {{-date}}`, {
                  date: rowData.utcEnd ? formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(rowData.utcEnd), timezoneToUse)
                  : formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiPrague(rowData.distributionValidTill), timezoneToUse),
                })}
                <COTimezone dataTestId={`carrier-demands-row-${applicationId}-validTill-timezone`} timezoneId={timezoneToUse} />
              </small>
              <small className="place" data-testid={`carrier-demands-row-${applicationId}-customerCompanyName`}>{rowData.customerCompanyName}</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const expeditionStartBodyTemplate = (rowData: QuotePreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    return <ExpeditionListStart label={t(`Demands.expedition_start`, `Expected start:`)}
                                expeditionStart={rowData.expeditionStart} userTimezoneId={timezoneToUse} valueTestId={`carrier-demands-row-${applicationId}-expeditionStart`}/>;
  };

  const routeBodyTemplate = (rowData: QuotePreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    return (
      <div className="locations">
        <ExpeditionLocation
          locationPoint={t(`Demands.from`, `From`)}
          locationAddress={`${rowData.locations[0].locationName}, ${rowData.locations[0].zipCode}, ${rowData.locations[0].city}, (${rowData.locations[0].country})`}
          wrapper="LARGE"
          timezoneId={`${rowData.locations[0].timezoneId}`}
          dataTestIdLocationName={`carrier-demands-row-${applicationId}-route-from`}
        />
        <ExpeditionLocation
          locationPoint={t(`Demands.to`, `To`)}
          locationAddress={`${rowData.locations[rowData.locations.length - 1].locationName}, ${rowData.locations[rowData.locations.length - 1].zipCode}, ${
            rowData.locations[rowData.locations.length - 1].city
          }, (${rowData.locations[rowData.locations.length - 1].country})`}
          wrapper="LARGE"
          timezoneId={`${rowData.locations[rowData.locations.length - 1].timezoneId}`}
          dataTestIdLocationName={`carrier-demands-row-${applicationId}-route-to`}
        />
        <div className="p-d-flex p-flex-nowrap">
          <div className="p-mr-1 location-point"></div>
          <div className="p-d-flex p-flex-wrap">{`${rowData.distance} km`}</div>
        </div>
      </div>
    );
  };

  const typeShipmentsBodyTemplate = (rowData: QuotePreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    return (
      <>
        <div>
          <div className="flex flex-row gap-0.5 items-center">
            <span data-testid={`carrier-demands-row-${applicationId}-expeditionType`} data-expedition-type={rowData.expeditionType}>
              {rowData.expeditionType ? t(`Demands.${rowData.expeditionType}`, rowData.expeditionType) : tr('global.expeditionTypeNotSpecified', 'Neuvedeno')}
            </span>
            <NoteIcon applicationId={rowData.applicationId} note={rowData.note} />
            <ExtraCostIcon extraCosts={rowData.extraCosts} applicationId={rowData.applicationId} />
            <span id={`route_${rowData.applicationId}`} className={'pi pi-map'}></span>
            {/*// @ts-ignore*/}
            <Tooltip target={`#route_${rowData.applicationId}`} style={{ maxWidth: '530px' }}>
              {rowData.locations.map((location, locationIndex) => {
                return (
                  <div key={locationIndex}>
                    {tr(`AllExpedition.locationInfo`, `Location {{order}}: {{locationName}}`, {
                      order: locationIndex + 1,
                      locationName: location.locationName,
                    })}
                  </div>
                );
              })}
            </Tooltip>
          </div>
          {rowData.truckType && (
            <div className="p-pb-1 type-shipments">
              <small data-testid={`carrier-demands-row-${applicationId}-truckType`}>{rowData.truckType}</small>
            </div>
          )}

          <div className="p-pb-1 type-shipments">
            <small data-testid={`carrier-demands-row-${applicationId}-cargoType`}>{rowData.cargoType}</small>
          </div>
        </div>
      </>
    );
  };

  const navBodyTemplate = (rowData: QuotePreviewOutDTO) => {
    return (
      <div>
        <ButtonLayout>
          {/*<Link href={`/carrier/demand/${rowData.companyId}-${rowData.applicationId}`}>*/}
          <Link
            to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierDemandDetailByQuoteId(rowData.quoteId))}>
            <a
              className="btn-detail p-button p-button-rounded p-button-outlined secondary-btn"
              data-testid={`carrier-demands-open-${rowData.quoteId}`}
            >
              {t('Demands.detail', 'Detail')}
            </a>
          </Link>
        </ButtonLayout>
      </div>
    );
  };

  interface FilterType {
    pseudoStates?: PseudoStatesEnum[];
    loadingDate?: Date;
    unloadingDate?: Date;
    locationName?: string;
    applicationId?: string;
    userTimezoneId: string;
  }

  const { Filter, applyFilterValue, filter, applyPaging, page } = useFilter<FilterType>({
    onLoad: (page: FilterPage, filter: FilterType) => {
      loadData(page.pageSize, page.pageNumber, filter);
    },
    filterName: 'demands',
    defaultFilter: { userTimezoneId: getBrowserTimezone() } as FilterType,
    arrayFields: ['pseudoStates'],
  });

  const { i18n } = useTranslation();

  return (
    <>
      <div className={'flex flex-col gap-2'}>
        <BreadCrumbComponent items={[{ label: tr(`Demands.demands`, `Demands`) }]} />
        <COHeadline title={tr(`Demands.demands`, `Demands`)} variant={'h1'} />
      </div>

      <Filter />
      <Grid>
        <div className={'field'}>
          <InputText
            value={filter?.applicationId}
            onChange={e => applyFilterValue('applicationId', e.target.value)}
            placeholder={tr(`Demands.applicationId`, `Application ID`)}
            data-testid="carrier-demands-filter-applicationId"
          />{' '}
        </div>
        <div className={'field'}>
          <MultiSelect
            value={filter?.pseudoStates}
            options={Object.keys(DemandListPseudoState).map(state => {
              return {
                code: state,
                name: t(`Demands.expeditionStage${state}`, state),
              };
            })}
            onChange={e => {
              applyFilterValue('pseudoStates', e.value);
            }}
            placeholder={t(`Demands.expeditionStage`, `Expedition stage`)}
            className="filter-table"
            showClear={true}
            optionLabel={'name'}
            optionValue={'code'}
          />
        </div>

        <div className={'field'}>
            <Calendar
              value={filter?.loadingDate instanceof Date ? filter.loadingDate : (filter?.loadingDate ? new Date(filter.loadingDate) : null)}
              onChange={e => applyFilterValue('loadingDate', e.value)}
              dateFormat={shortDateFormat}
              placeholder={tr(`Demands.loading`, `Loading`)}
              showIcon={true}
              locale={i18n.language}
              showButtonBar={true}
              onClearButtonClick={() => applyFilterValue('loadingDate', null)}
            />
        </div>
        <div className={'field'}>
            <Calendar
              value={filter?.unloadingDate instanceof Date ? filter.unloadingDate : (filter?.unloadingDate ? new Date(filter.unloadingDate) : null)}
              onChange={e => applyFilterValue('unloadingDate', e.value)}
              dateFormat={shortDateFormat}
              placeholder={tr(`Demands.unloading`, `Unloading`)}
              showIcon={true}
              locale={i18n.language}
              showButtonBar={true}
              onClearButtonClick={() => applyFilterValue('unloadingDate', null)}
            />
        </div>
        <div className={'field'}>
          <InputText
            value={filter?.locationName}
            onChange={e => applyFilterValue('locationName', e.target.value)}
            placeholder={tr(`Demands.locationName`, `Location name`)}
          />{' '}
        </div>
      </Grid>

      {pageData && (
        <>
          {/*// @ts-ignore*/}
          <DataTable
            emptyMessage={t(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}
            className="default-exp-table demands-table"
            value={pageData.data}
            rows={pageData.size}
            paginator={true}
            lazy={true}
            totalRecords={pageData.total}
            onPage={onPage}
            first={(pageData.page - 1) * pageData.size}
          >
            <Column className="demands-table__id text-blue" field="id" body={stageBodyTemplate}></Column>
            <Column className="demands-table__price" body={typePriceBodyTemplate}></Column>
            <Column className="demands-table__shipments" body={typeShipmentsBodyTemplate}></Column>
            <Column className="demands-table__route" body={routeBodyTemplate}></Column>
            <Column className="demands-table__expedition_start" body={expeditionStartBodyTemplate}></Column>
            <Column className="demands-table__nav" body={navBodyTemplate}></Column>
          </DataTable>
        </>
      )}
    </>
  );
};

const Grid = styled.div`
  display: flex;
  gap: 0.2rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  align-items: stretch; /* Makes all divs stretch to the same height */

  .field {
    display: flex;
    flex-direction: column;
  }

  /* Target the p-dropdown specifically to match the height of other inputs */

  .p-dropdown {
    height: 100%;
    display: flex;
    align-items: center;
  }

  /* Ensure input controls have consistent height */

  .p-inputtext, .p-calendar, .p-dropdown {
    height: 2.5rem; /* Adjust this value based on your design */
  }

  /* Make sure the calendar icon button maintains height */

  .p-calendar .p-button {
    height: 100%;
  }
`;

export default Demands;
